import { Observable } from 'rxjs';
import { getInfoPathSolutionName } from '@shared/helpers/infopath.helper';
import { MyVerbale } from '../models/interfaces/revisione.interface';
import { INFOPATH_HEADER_SIZE, INFOPATH_MAGIC_B64, XHTML_NS, XSI_NS } from './infopath-attachment.constants';

const INFOPATH_SOLUTION_PI = 'mso-infoPathSolution';

export interface ParsedVerbaleXml {
    verbale: Partial<MyVerbale>;
    /** InfoPath solution name (e.g. `VerbaleDiRevisioneMinisteroPDFV51-xsn`), `''` if absent. */
    solutionName: string;
}

/**
 * Decodes an InfoPath file attachment (base64-encoded binary with proprietary header)
 * into a native File object.
 *
 * Format: [4B magic][4B headerSize][4B version][4B reserved][4B fileSize]
 *         [4B nameLen (chars incl. null)][nameLen*2 B UTF-16LE name][file bytes]
 *
 * Returns null if the string is not a valid InfoPath attachment.
 */
export function decodeInfoPathAttachment(base64: string): File | null {
    if (!base64.startsWith(INFOPATH_MAGIC_B64)) return null;
    try {
        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        const view = new DataView(bytes.buffer);
        const fileSize = view.getUint32(16, true);
        const nameLen = view.getUint32(20, true); // chars including null terminator
        const nameBytes = bytes.slice(INFOPATH_HEADER_SIZE, INFOPATH_HEADER_SIZE + (nameLen - 1) * 2);
        const name = new TextDecoder('utf-16le').decode(nameBytes);
        const dataStart = INFOPATH_HEADER_SIZE + nameLen * 2;
        const fileData = bytes.slice(dataStart, dataStart + fileSize);
        return new File([fileData], name, { type: _mimeFromName(name) });
    } catch {
        return null;
    }
}

function _mimeFromName(name: string): string {
    const ext = name.split('.').pop()?.toLowerCase() ?? '';
    const map: Record<string, string> = {
        pdf: 'application/pdf',
        xml: 'application/xml',
        xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        xls: 'application/vnd.ms-excel',
        docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        doc: 'application/msword',
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        png: 'image/png',
    };
    return map[ext] ?? 'application/octet-stream';
}

/**
 * Recursively walks a parsed MyVerbale object and returns the path+value
 * of every string field that contains an InfoPath attachment.
 */
export function collectInfoPathAttachmentPaths(
    obj: unknown,
    path: string[] = [],
): Array<{ path: string[]; value: string }> {
    if (typeof obj === 'string' && obj.startsWith(INFOPATH_MAGIC_B64)) {
        return [{ path, value: obj }];
    }
    if (Array.isArray(obj)) {
        return obj.flatMap((item, i) => collectInfoPathAttachmentPaths(item, [...path, String(i)]));
    }
    if (obj !== null && typeof obj === 'object') {
        return Object.entries(obj as Record<string, unknown>).flatMap(([key, val]) =>
            collectInfoPathAttachmentPaths(val, [...path, key]),
        );
    }
    return [];
}

/**
 * Reads a File (.xml) and emits a Partial<MyVerbale> matching
 * the JSON structure expected by the NgRx store.
 */
export const parseVerbaleFileToMyVerbale = (file: File): Observable<ParsedVerbaleXml> => {
    return new Observable((observer) => {
        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const xmlString = event.target?.result as string;
                observer.next(parseVerbaleXmlToMyVerbale(xmlString));
                observer.complete();
            } catch (err) {
                observer.error(err);
            }
        };
        reader.onerror = () => observer.error(new Error(`Errore lettura file: ${file.name}`));
        reader.readAsText(file, 'UTF-8');
        return () => reader.abort();
    });
};

/**
 * Parses a verbale XML string and returns a Partial<MyVerbale> matching
 * the JSON structure expected by the NgRx store.
 *
 * Rules applied:
 *  - my:FooBar         → my_FooBar
 *  - my:Foo-Bar        → my_Foo__Bar  (hyphen → double underscore)
 *  - unprefixed elem   → key as-is    (e.g. TotaleB7NonModif)
 *  - xsi:nil="true"    → ""
 *  - El-prefixed child → always an array, even with a single occurrence
 *  - multiple siblings with same tag → array
 *
 * The root element of the XML (<my:Verbale>) is unwrapped: only its children
 * are returned, so the result maps directly onto MyVerbale.
 */
export function parseVerbaleXmlToMyVerbale(xmlString: string): ParsedVerbaleXml {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlString, 'application/xml');

    const parserError = doc.querySelector('parsererror');
    if (parserError) {
        throw new Error(`XML parse error: ${parserError.textContent}`);
    }

    const solutionPi = Array.from(doc.childNodes).find(
        (n) => n.nodeType === Node.PROCESSING_INSTRUCTION_NODE && (n as ProcessingInstruction).target === INFOPATH_SOLUTION_PI,
    ) as ProcessingInstruction | undefined;

    // doc.documentElement is <my:Verbale> — unwrap it directly
    return {
        verbale: transformChildren(doc.documentElement) as Partial<MyVerbale>,
        solutionName: getInfoPathSolutionName(solutionPi?.data ?? ''),
    };
}

// ---------------------------------------------------------------------------
// Core recursive transformer
// ---------------------------------------------------------------------------

function transformElement(el: Element): unknown {
    if (el.getAttributeNS(XSI_NS, 'nil') === 'true') {
        return '';
    }

    const children = Array.from(el.children);

    if (children.length === 0) {
        const emptyArrayKey = ARRAY_CONTAINER_EMPTY_KEYS.get(el.localName);
        if (emptyArrayKey !== undefined) {
            return { [emptyArrayKey]: [] };
        }
        return el.textContent?.trim() ?? '';
    }

    // Rich text InfoPath: figli XHTML (div/p/span) → una stringa, una riga per nodo
    // ponytail: <br/> non genera a capo; aggiungere se compare nei verbali reali
    if (children.some((c) => c.namespaceURI === XHTML_NS)) {
        return Array.from(el.childNodes)
            .map((n) => n.textContent?.trim() ?? '')
            .filter((t) => t !== '')
            .join('\n');
    }

    return transformChildren(el);
}

function transformChildren(el: Element): Record<string, unknown> {
    const children = Array.from(el.children);
    const result: Record<string, unknown> = {};

    // Count occurrences per tag so repeated siblings become arrays
    const tagCount = new Map<string, number>();
    for (const child of children) {
        tagCount.set(child.tagName, (tagCount.get(child.tagName) ?? 0) + 1);
    }

    const visited = new Set<string>();

    for (const child of children) {
        if (visited.has(child.tagName)) continue;
        visited.add(child.tagName);

        const key = toJsonKey(child);
        const forceArray = tagCount.get(child.tagName)! > 1 || isArrayItem(child.localName);

        if (forceArray) {
            const siblings = children.filter((c) => c.tagName === child.tagName);
            result[key] = siblings.map((s) => transformElement(s));
        } else {
            result[key] = transformElement(child);
        }
    }

    return result;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Converts a namespace-prefixed XML element name to its JSON key:
 *   my:FooBar        → my_FooBar
 *   my:Foo-Bar       → my_Foo__Bar
 *   unprefixed elem  → FooBar (unchanged)
 */
function toJsonKey(el: Element): string {
    const localName = el.localName.replace(/-/g, '__');
    return el.prefix ? `${el.prefix}_${localName}` : localName;
}

// Maps container element localNames to the JSON key of their single array child.
// When a container element has no XML children (empty), we return { key: [] }
// instead of "" so downstream form patching receives a proper empty array.
const ARRAY_CONTAINER_EMPTY_KEYS = new Map<string, string>([
    // Sezione I — base-sociale / natura-mutualistica
    ['AderentiA', 'my_ElAderentiA'],
    ['DichiarSostit', 'my_ElDichiarSostit'],
    // Sezione I — base-sociale lavoratori
    ['ContrattiUsati', 'my_ElContrattiUsati'],
    ['ContrattiUsatiSociLavoratori', 'my_ElContrattiUsatiSociLavoratori'],
    // Sezione I — dati-bilancio
    ['EntiErogatori', 'my_ElEnteErogatore'],
    ['SocietaPartecipate', 'my_ElSocietaPartecipate'],
    ['RelazCertifAllegata', 'my_ElRelazCertifAllegata'],
    ['NotaIntegrativaAllegata', 'my_ElNotaIntegrativaAllegata'],
    ['CopiaBilancioAllegato', 'my_ElCopiaBilancioAllegato'],
    ['ContrattoSocietaCertificazione', 'my_ElContrattoSocietaCertificazione'],
    // Sezione I — conclusioni
    ['IrregSanabili', 'my_ElIrregSanabili'],
    ['SanzioniAccessorieRilevazione', 'my_ElSanzioniAccessorieRilevazione'],
    ['OsservazioniRespPrecRev', 'my_elOsservazioniRespPrecRev'],
    // Sezione I — scheda coop sociali
    ['EntiConv', 'my_ElEntiConv'],
    // amm-controllo organs (SPD dualistico)
    ['SpdConsiglioSorveglianza', 'my_SpdElemConsiglioSorveglianza'],
    ['SpdConsiglioGestione', 'my_SpdElemConsiglioGestione'],
    // amm-controllo organs (SPM monistico)
    ['SpmComitatoGestione', 'my_SpmElemComitatoGestione'],
    // amm-controllo organs (ordinario / SRL)
    ['AllConsiglioAmministrazione', 'my_AllElemConsiglioAmministrazione'],
    ['AllCollegioSindacale', 'my_AllElemCollegioSindacale'],
    ['AllLiquidatori', 'my_AllElemLiquidatori'],
    ['AllRevisoriControlloContabile', 'my_AllElemRevisoriControlloContabile'],
    // Sezione II
    ['AderentiAII', 'my_ElAderentiAII'],
    ['RisultanzeAccert', 'my_ElRisultanzeAccert'],
    ['SanzioniAccessorie', 'my_ElSanzioniAccessorie'],
    ['AltriAllegatiAccertamento', 'my_AltroAllegatoAccertamento'],
    // Globali
    ['Warning', 'my_ElWarning'],
    ['RTCalcoloMoraSanzione', 'my_RTRigaCalcoloMoraSanzione'],
    ['AllContributoAssociativo', 'my_AllElemContributoAssociativo'],
    ['AllContributoRevPrec', 'my_AllElemContributoRevPrec'],
    ['CertificazioniPassate', 'my_ElCertificazioniPassate'],
    ['AltriAllegati', 'my_AltroAllegato'],
]);

// All array-item localNames that do NOT start with El/el.
// Derived from every [] field in revisione.interface.ts that maps to an XML element.
const ARRAY_ITEM_NAMES = new Set([
    // AllElem* group (amm-controllo organs)
    'AllElemConsiglioAmministrazione',
    'AllElemCollegioSindacale',
    'AllElemLiquidatori',
    'AllElemRevisoriControlloContabile',
    'AllElemContributoAssociativo',
    'AllElemContributoRevPrec',
    // SpdElem* group (SPD dualistic organs)
    'SpdElemConsiglioSorveglianza',
    'SpdElemConsiglioGestione',
    // SpmElem* group (SPM monistic organs)
    'SpmElemComitatoGestione',
    // Standalone names
    'AllAmminUnico',
    'AltroAllegato',
    'AltroAllegatoAccertamento',
    'RTRigaCalcoloMoraSanzione',
]);

// El* names that are singular objects in the interface (NOT arrays).
// Must be excluded from the startsWith('El') heuristic.
// ElTermineCariche → MyTermineCariche.my_ElTermineCariche (singular)
// ElemErrori      → MyErrori.my_ElemErrori (singular)
const SINGULAR_EL_NAMES = new Set(['ElTermineCariche', 'ElemErrori']);

/**
 * Returns true when this element is always an array item, even with a single occurrence.
 * - El* / el* covers the vast majority (ElAderentiA, elOsservazioniRespPrecRev, …)
 *   with explicit exclusions for singular El* objects (SINGULAR_EL_NAMES)
 * - ARRAY_ITEM_NAMES covers the remaining known cases from revisione.interface.ts
 */
function isArrayItem(localName: string): boolean {
    return ((localName.startsWith('El') || localName.startsWith('el')) && !SINGULAR_EL_NAMES.has(localName)) || ARRAY_ITEM_NAMES.has(localName);
}

